import { debounce } from 'lodash'
import { Controller } from 'stimulus'
import Swiper, { EffectFade } from 'swiper'

Swiper.use([EffectFade])

export default class extends Controller {
  static targets = ['traySlider', 'trayWrapper', 'traySlide', 'contentSlider']

  connect() {
    this.handleTap = this.handleTap.bind(this);
    this.handleTraySlideChange = this.handleTraySlideChange.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    this.lastWidth = window.innerWidth;
    this.initMenu();

    this.updateJustify();
  }

  initMenu() {
    this.initMenuTray();
    this.initMenuContent();

    if (!this.menuTray || !this.menuContent) return;

    this.resizeHandler = debounce(this.handleResize.bind(this), 200);
    window.addEventListener('resize', this.resizeHandler);
  }

  initMenuTray() {
    if (this.menuTray instanceof Swiper) {
      this.menuTray.destroy();
      this.menuTray = null;
    }

    if (this.traySliderTarget) {
      this.menuTray = new Swiper(this.traySliderTarget, {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: 60,
        speed: 100,
        slideActiveClass: 'active',
        allowTouchMove: true,
        breakpoints: {
          1024: {
            freeMode: true,
            spaceBetween: 80,
          }
        }
      });
    }

    this.traySlideTargets.forEach((slide, index) => {
      slide.addEventListener('click', () => this.handleTap(index));
    });
    this.menuTray.on('slideChange', this.handleTraySlideChange);
    this.menuTray.on('transitionEnd', this.handleTransitionEnd);
    this.updateTraySlideAccessibility();
  }

  initMenuContent() {
    if (this.menuContent instanceof Swiper) {
      this.menuContent.destroy();
      this.menuContent = null;
    }

    if (this.contentSliderTarget) {
      this.menuContent = new Swiper(this.contentSliderTarget, {
        slidesPerView: 1,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        autoHeight: true,
        freeMode: false,
        centeredSlides: false,
        threshold: 50,
        resistance: true,
        resistanceRatio: 0.85,
        spaceBetween: 0,
        speed: 100,
        allowTouchMove: false,
        breakpoints: {
          700: {
            threshold: 100,
            centeredSlides: true,
            allowTouchMove: false,
            sliderPerView: 'auto',
          },
          1280: {
            allowTouchMove: false,
          }
        }
      });
    }
  }

  handleResize() {
    let currentWidth = window.innerWidth;
    if (currentWidth === this.lastWidth) return;
    this.lastWidth = currentWidth;

    this.updateJustify();
    this.initMenuTray();
  }

  handleTap(index) {
    const { freeMode } = this.menuTray.params;
    if (index === undefined) return;

    this.selectMenu(index);

    if (freeMode) return;

    if (this.isElementHorizontallyVisibleInParent(this.traySlideTargets[index], this.traySliderTarget, { partialVisibility: false })) return;
    this.menuTray.slideTo(index);
  }

  handleTraySlideChange(e) {
    const { freeMode } = this.menuTray.params;

    if (freeMode) return;

    const selectedSlide = this.traySlideTargets.find((slide, i) => slide.classList.contains('selected'));

    if (this.isElementHorizontallyVisibleInParent(selectedSlide, this.traySliderTarget, { partialVisibility: true, visibilityThreshold: 0.1 })) return;
    this.selectMenu(e.activeIndex);
  }

  handleTransitionEnd() {
    this.updateTraySlideAccessibility();
  }

  selectMenu(index) {
    this.traySlideTargets.forEach((slide, i) => {
      if (index === i) {
        slide.classList.add('selected');
        return;
      }
      slide.classList.remove('selected');
    });

    this.menuContent.slideTo(index);
  }

  updateJustify() {
    const container = this.traySliderTarget;
    const wrapper = this.trayWrapperTarget;
    const slides = this.traySlideTargets;

    if (!container || !wrapper || !slides?.length) return false;

    const containerWidth = container.clientWidth;
    const marginWidth = parseFloat(window.getComputedStyle(slides[0]).marginRight);
    let wrapperWidth = slides.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
    wrapperWidth = wrapperWidth + (slides.length - 1) * marginWidth;

    if (wrapperWidth <= containerWidth) {
      wrapper.classList.add('all-visible');
      return;
    }

    wrapper.classList.remove('all-visible');
  }

  updateTraySlideAccessibility() {
    this.traySlideTargets.forEach((slide) => {
      const makeAccessible = this.isElementHorizontallyVisibleInParent(slide, this.traySliderTarget, { partialVisibility: true, visibilityThreshold: 0.5 });
      makeAccessible ? slide.removeAttribute('aria-hidden') : slide.setAttribute('aria-hidden', true);
      makeAccessible ? slide.removeAttribute('tabindex') : slide.setAttribute('tabindex', "-1");
    });
  }

  isElementHorizontallyVisibleInParent(element, parent, options = {}) {
    if (!element || !parent) return false;

    const { partialVisibility = false, visibilityThreshold = 1 } = options;

    const elementRect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    const visibleWidth = Math.max(0, Math.min(elementRect.right, parentRect.right) - Math.max(elementRect.left, parentRect.left));
    const elementWidth = elementRect.width;
    const visibilityRatio = visibleWidth / elementWidth;

    if (partialVisibility) return visibilityRatio >= visibilityThreshold;

    return visibleWidth >= elementWidth;
  }

  disconnect() {
    window.removeEventListener('resize', this.resizeHandler)
  }
}
